import { Head as SeoHead } from 'components/SEO';
import { JSONLDSchema } from 'components/StructuredData/types';
import { graphql, PageProps } from 'gatsby';
import React, { useMemo } from 'react';

import {
  KitCatalogNode,
  N_TESTS,
  categoryByKeyOf,
  indexKindOf,
  kitProductsFrom,
} from '../../components/Everywhere/catalog';
import {
  EVERYWHERE_OG_IMAGE_URL,
  azCrumbs,
  everywhereAbsoluteUrl,
  everywhereBreadcrumbList,
} from '../../components/Everywhere/seo';
import IndexPage from '../../components/Everywhere/v2/index/IndexPage';
import EverywhereV2Shell from '../../components/Everywhere/v2/Shell';

/**
 * Crawlable A–Z hub (S3): every test as a real link, optionally filtered by
 * category (`?cat=thyroid`) or kind (`?az=1&kind=panel`).
 */
type EverywhereTestsData = {
  allSpreeProduct: { nodes: KitCatalogNode[] };
};

const EverywhereTestsIndexPage: React.FC<PageProps<EverywhereTestsData>> = ({
  data,
  location,
}) => {
  const { category, kind, query } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const categoryKey = params.get('cat');
    const kindKey = indexKindOf(params.get('kind'));
    let category: string | null = null;
    if (!kindKey && categoryKey && categoryByKeyOf(categoryKey)) {
      category = categoryKey;
    }
    return {
      category,
      kind: kindKey,
      query: (params.get('q') || '').trim(),
    };
  }, [location.search]);
  const kits = kitProductsFrom(data.allSpreeProduct.nodes);
  return (
    <EverywhereV2Shell screen="index">
      <IndexPage category={category} kind={kind} kits={kits} query={query} />
    </EverywhereV2Shell>
  );
};

export default EverywhereTestsIndexPage;

const INDEX_URL = everywhereAbsoluteUrl('/everywhere/tests/');

export const Head = (): JSX.Element => (
  <SeoHead
    title={`All ${N_TESTS} Tests, A to Z | Everlywell Everywhere`}
    description="Browse every test you can order online through Everlywell Everywhere, including walk-in lab testing and at-home kits."
    canonicalUrl="everywhere/tests"
    url={INDEX_URL}
    image={EVERYWHERE_OG_IMAGE_URL}
    structuredDataOverride={
      everywhereBreadcrumbList(azCrumbs()) as unknown as Partial<JSONLDSchema>
    }
  />
);

export const query = graphql`
  query EverywhereKitsIndex {
    allSpreeProduct {
      nodes {
        name
        slug
        displayable
        variants {
          is_master
          price
        }
        contentfulProductInfo {
          displayName
          shortDescription
          outOfStock
          collectionMethods {
            ... on ContentfulCollectionMethod {
              name
              v2Name
            }
          }
          productCardImage {
            description
            file {
              url
            }
          }
          boxImage {
            description
            file {
              url
            }
          }
        }
      }
    }
  }
`;
